import { deviceApi } from '../api/device.api';
import { deviceDataApi } from '../api/device-data.api';
import { DeviceSummary } from '../models/device';
import { Reading, ReadingWindow } from '../models/reading';
import { useAsync } from './useAsync';

export interface DeviceDetail {
  device?: DeviceSummary;
  /** Last reading ever received: `null` when the device has none, `undefined` while loading. */
  latest?: Reading | null;
  /** Readings in the period: `undefined` while it loads, so the previous period's data is never shown. */
  window?: ReadingWindow & { from: Date; to: Date };
  loading: boolean;
  error?: unknown;
  reload: () => void;
}

export type FetchReadingWindow = (uuid: string, from: Date, to: Date) => Promise<ReadingWindow>;

/** The device, its last reading and its readings in [from, to), read with `fetchWindow` (pass a stable function). */
export const useDeviceDetail = (
  uuid: string,
  from: Date,
  to: Date,
  fetchWindow: FetchReadingWindow = deviceDataApi.getByTimeRange,
): DeviceDetail => {
  const info = useAsync(async () => {
    const [device, latest] = await Promise.all([deviceApi.getByUuid(uuid), deviceDataApi.getLatest(uuid)]);
    return { device, latest: latest ?? null };
  }, [uuid]);

  const fromMs = from.getTime();
  const toMs = to.getTime();
  const data = useAsync(async () => {
    const result = await fetchWindow(uuid, new Date(fromMs), new Date(toMs));
    return { ...result, from: new Date(fromMs), to: new Date(toMs) };
  }, [uuid, fromMs, toMs, fetchWindow]);

  return {
    device: info.data?.device,
    latest: info.data?.latest,
    window: data.loading ? undefined : data.data,
    loading: info.loading || data.loading,
    error: info.error ?? data.error,
    reload: () => {
      info.reload();
      data.reload();
    },
  };
};
